import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || '/api';

/**
 * VehicleSelector — Autocomplete component for selecting vehicle make + model.
 *
 * Props:
 *   value: { make, model, year, license_plate, vin?, color? }
 *   onChange: (vehicle) => void
 *   t: translation object
 *   showVin: boolean (optional, for admin)
 *   showColor: boolean (optional, for admin)
 */
export default function VehicleSelector({ value, onChange, t, showVin = false, showColor = false }) {
  const bk = t.booking || {};

  // Make search state
  const [makeQuery, setMakeQuery] = useState(value.make || '');
  const [makeSuggestions, setMakeSuggestions] = useState([]);
  const [makeOpen, setMakeOpen] = useState(false);
  const [makeSelected, setMakeSelected] = useState(!!value.make && value.make !== 'Other');
  const [selectedMakeId, setSelectedMakeId] = useState(null);
  const [isOtherMake, setIsOtherMake] = useState(value.make === 'Other');
  const makeRef = useRef(null);
  const makeTimerRef = useRef(null);

  // Model search state
  const [modelQuery, setModelQuery] = useState(value.model || '');
  const [modelSuggestions, setModelSuggestions] = useState([]);
  const [modelOpen, setModelOpen] = useState(false);
  const [modelSelected, setModelSelected] = useState(!!value.model);
  const [isOtherModel, setIsOtherModel] = useState(false);
  const modelRef = useRef(null);
  const modelTimerRef = useRef(null);

  // Keyboard navigation
  const [makeHighlight, setMakeHighlight] = useState(-1);
  const [modelHighlight, setModelHighlight] = useState(-1);

  const labelStyle = { display: 'block', color: '#94A3B8', fontSize: '0.75rem', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '0.5rem', fontFamily: "'Barlow', sans-serif" };

  // Fetch makes
  const fetchMakes = useCallback(async (q) => {
    try {
      const params = q ? { q } : { popular: 'true' };
      const res = await axios.get(`${API}/vehicle-catalog/makes`, { params });
      setMakeSuggestions(res.data.makes || []);
    } catch {
      setMakeSuggestions([]);
    }
  }, []);

  // Fetch models
  const fetchModels = useCallback(async (makeId, q) => {
    if (!makeId) return;
    try {
      const params = { make_id: makeId };
      if (q) params.q = q;
      const res = await axios.get(`${API}/vehicle-catalog/models`, { params });
      setModelSuggestions(res.data.models || []);
    } catch {
      setModelSuggestions([]);
    }
  }, []);

  // On make input focus — show popular makes
  const handleMakeFocus = () => {
    setMakeOpen(true);
    setMakeHighlight(-1);
    if (!makeQuery) fetchMakes('');
  };

  // On make input change — debounced search
  const handleMakeChange = (val) => {
    setMakeQuery(val);
    setMakeSelected(false);
    setIsOtherMake(false);
    setSelectedMakeId(null);
    setMakeOpen(true);
    setMakeHighlight(-1);
    // Reset model
    setModelQuery('');
    setModelSelected(false);
    setIsOtherModel(false);
    setModelSuggestions([]);
    onChange({ ...value, make: val, model: '' });

    clearTimeout(makeTimerRef.current);
    makeTimerRef.current = setTimeout(() => fetchMakes(val), 300);
  };

  // Select a make from suggestions
  const selectMake = (make) => {
    if (make === '__other__') {
      setMakeQuery('');
      setIsOtherMake(true);
      setMakeSelected(false);
      setSelectedMakeId(null);
      setMakeOpen(false);
      onChange({ ...value, make: '', model: '' });
      setModelQuery('');
      setModelSelected(false);
      setIsOtherModel(false);
      return;
    }
    setMakeQuery(make.name);
    setMakeSelected(true);
    setSelectedMakeId(make.id);
    setMakeOpen(false);
    onChange({ ...value, make: make.name, model: '' });
    // Reset model
    setModelQuery('');
    setModelSelected(false);
    setIsOtherModel(false);
    // Pre-fetch models
    fetchModels(make.id, '');
  };

  // On model input focus
  const handleModelFocus = () => {
    setModelOpen(true);
    setModelHighlight(-1);
    if (selectedMakeId && !modelQuery) fetchModels(selectedMakeId, '');
  };

  // On model input change
  const handleModelChange = (val) => {
    setModelQuery(val);
    setModelSelected(false);
    setIsOtherModel(false);
    setModelOpen(true);
    setModelHighlight(-1);
    onChange({ ...value, model: val });

    clearTimeout(modelTimerRef.current);
    modelTimerRef.current = setTimeout(() => fetchModels(selectedMakeId, val), 300);
  };

  // Select a model
  const selectModel = (model) => {
    if (model === '__other__') {
      setModelQuery('');
      setIsOtherModel(true);
      setModelSelected(false);
      setModelOpen(false);
      onChange({ ...value, model: '' });
      return;
    }
    setModelQuery(model.name);
    setModelSelected(true);
    setModelOpen(false);
    onChange({ ...value, model: model.name });
  };

  // Keyboard navigation for make
  const handleMakeKeyDown = (e) => {
    const items = [...makeSuggestions, '__other__'];
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setMakeHighlight(prev => Math.min(prev + 1, items.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setMakeHighlight(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && makeHighlight >= 0) {
      e.preventDefault();
      selectMake(items[makeHighlight]);
    } else if (e.key === 'Escape') {
      setMakeOpen(false);
    }
  };

  // Keyboard navigation for model
  const handleModelKeyDown = (e) => {
    const items = [...modelSuggestions, '__other__'];
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setModelHighlight(prev => Math.min(prev + 1, items.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setModelHighlight(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && modelHighlight >= 0) {
      e.preventDefault();
      selectModel(items[modelHighlight]);
    } else if (e.key === 'Escape') {
      setModelOpen(false);
    }
  };

  // Close dropdowns on outside click
  useEffect(() => {
    const handler = (e) => {
      if (makeRef.current && !makeRef.current.contains(e.target)) setMakeOpen(false);
      if (modelRef.current && !modelRef.current.contains(e.target)) setModelOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const dropdownStyle = {
    position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1050,
    background: '#111827', border: '1px solid rgba(148,163,184,0.15)',
    maxHeight: '250px', overflowY: 'auto', marginTop: '2px'
  };

  const itemStyle = (highlighted) => ({
    padding: '0.6rem 0.8rem', cursor: 'pointer', fontSize: '0.9rem', color: '#E8ECF0',
    background: highlighted ? 'rgba(212,43,43,0.1)' : 'transparent',
    borderBottom: '1px solid rgba(148,163,184,0.05)'
  });

  const otherItemStyle = (highlighted) => ({
    ...itemStyle(highlighted),
    color: '#94A3B8', fontStyle: 'italic', borderTop: '1px solid rgba(148,163,184,0.15)'
  });

  // Grouping: popular first, then rest
  const popularMakes = makeSuggestions.filter(m => m.is_popular);
  const otherMakes = makeSuggestions.filter(m => !m.is_popular);
  const hasBothGroups = popularMakes.length > 0 && otherMakes.length > 0 && !makeQuery;

  return (
    <div>
      <div className="row mb-3 cjpa-vehicle-row">
        {/* Make field */}
        <div className="col-12 col-md-6" ref={makeRef} style={{ position: 'relative' }}>
          <label style={labelStyle}>{bk.makeLabel || 'Make'}</label>
          {isOtherMake ? (
            <div>
              <input
                type="text"
                className="form-control"
                placeholder={bk.specifyPlaceholder || 'Enter vehicle make'}
                value={value.make}
                onChange={e => onChange({ ...value, make: e.target.value })}
              />
              <button type="button" onClick={() => { setIsOtherMake(false); setMakeQuery(''); onChange({ ...value, make: '', model: '' }); }}
                className="btn btn-link btn-sm" style={{ color: '#94A3B8', padding: 0, fontSize: '0.75rem', marginTop: '0.25rem' }}>
                {bk.backToSearch || 'Back to search'}
              </button>
            </div>
          ) : (
            <div>
              <input
                type="text"
                className="form-control"
                placeholder={bk.searchMake || 'Search make...'}
                value={makeQuery}
                onChange={e => handleMakeChange(e.target.value)}
                onFocus={handleMakeFocus}
                onKeyDown={handleMakeKeyDown}
                autoComplete="off"
              />
              {makeSelected && (
                <button type="button" onClick={() => { setMakeQuery(''); setMakeSelected(false); setSelectedMakeId(null); onChange({ ...value, make: '', model: '' }); setModelQuery(''); setModelSelected(false); }}
                  style={{ position: 'absolute', right: '1.2rem', top: '2.2rem', background: 'none', border: 'none', color: '#94A3B8', cursor: 'pointer', fontSize: '1rem' }}>&times;</button>
              )}
              {makeOpen && makeSuggestions.length > 0 && (
                <div style={dropdownStyle}>
                  {hasBothGroups && (
                    <div style={{ padding: '0.4rem 0.8rem', fontSize: '0.7rem', color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                      {bk.popularMakes || 'Popular Makes'}
                    </div>
                  )}
                  {(hasBothGroups ? popularMakes : makeSuggestions).map((m, i) => (
                    <div key={m.id} style={itemStyle(makeHighlight === i)} onClick={() => selectMake(m)}
                      onMouseEnter={() => setMakeHighlight(i)}>
                      {m.name}
                    </div>
                  ))}
                  {hasBothGroups && otherMakes.length > 0 && (
                    <>
                      <div style={{ padding: '0.4rem 0.8rem', fontSize: '0.7rem', color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.1em', borderTop: '1px solid rgba(148,163,184,0.15)' }}>
                        {bk.allMakes || 'All Makes'}
                      </div>
                      {otherMakes.map((m, i) => {
                        const idx = popularMakes.length + i;
                        return (
                          <div key={m.id} style={itemStyle(makeHighlight === idx)} onClick={() => selectMake(m)}
                            onMouseEnter={() => setMakeHighlight(idx)}>
                            {m.name}
                          </div>
                        );
                      })}
                    </>
                  )}
                  <div style={otherItemStyle(makeHighlight === makeSuggestions.length)}
                    onClick={() => selectMake('__other__')}
                    onMouseEnter={() => setMakeHighlight(makeSuggestions.length)}>
                    {bk.otherMake || 'Other (not listed)'}
                  </div>
                </div>
              )}
              {makeOpen && makeSuggestions.length === 0 && makeQuery.length > 0 && (
                <div style={dropdownStyle}>
                  <div style={{ padding: '0.8rem', color: '#64748B', fontSize: '0.85rem', textAlign: 'center' }}>
                    {bk.noMakesFound || 'No makes found'}
                  </div>
                  <div style={otherItemStyle(false)} onClick={() => selectMake('__other__')}>
                    {bk.otherMake || 'Other (not listed)'}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Model field */}
        <div className="col-12 col-md-6" ref={modelRef} style={{ position: 'relative' }}>
          <label style={labelStyle}>{bk.modelLabel || 'Model'}</label>
          {isOtherMake || isOtherModel ? (
            <div>
              <input
                type="text"
                className="form-control"
                placeholder={bk.specifyPlaceholder || 'Enter vehicle model'}
                value={value.model}
                onChange={e => onChange({ ...value, model: e.target.value })}
              />
              {!isOtherMake && isOtherModel && (
                <button type="button" onClick={() => { setIsOtherModel(false); setModelQuery(''); onChange({ ...value, model: '' }); }}
                  className="btn btn-link btn-sm" style={{ color: '#94A3B8', padding: 0, fontSize: '0.75rem', marginTop: '0.25rem' }}>
                  {bk.backToSearch || 'Back to search'}
                </button>
              )}
            </div>
          ) : (
            <div>
              <input
                type="text"
                className="form-control"
                placeholder={bk.searchModel || 'Search model...'}
                value={modelQuery}
                onChange={e => handleModelChange(e.target.value)}
                onFocus={handleModelFocus}
                onKeyDown={handleModelKeyDown}
                disabled={!makeSelected}
                autoComplete="off"
              />
              {modelSelected && (
                <button type="button" onClick={() => { setModelQuery(''); setModelSelected(false); onChange({ ...value, model: '' }); }}
                  style={{ position: 'absolute', right: '1.2rem', top: '2.2rem', background: 'none', border: 'none', color: '#94A3B8', cursor: 'pointer', fontSize: '1rem' }}>&times;</button>
              )}
              {modelOpen && modelSuggestions.length > 0 && (
                <div style={dropdownStyle}>
                  {modelSuggestions.map((m, i) => (
                    <div key={m.id} style={itemStyle(modelHighlight === i)} onClick={() => selectModel(m)}
                      onMouseEnter={() => setModelHighlight(i)}>
                      {m.name}
                    </div>
                  ))}
                  <div style={otherItemStyle(modelHighlight === modelSuggestions.length)}
                    onClick={() => selectModel('__other__')}
                    onMouseEnter={() => setModelHighlight(modelSuggestions.length)}>
                    {bk.otherModel || 'Other (not listed)'}
                  </div>
                </div>
              )}
              {modelOpen && modelSuggestions.length === 0 && modelQuery.length > 0 && makeSelected && (
                <div style={dropdownStyle}>
                  <div style={{ padding: '0.8rem', color: '#64748B', fontSize: '0.85rem', textAlign: 'center' }}>
                    {bk.noModelsFound || 'No models found'}
                  </div>
                  <div style={otherItemStyle(false)} onClick={() => selectModel('__other__')}>
                    {bk.otherModel || 'Other (not listed)'}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Year + License Plate */}
      <div className="row mb-4 cjpa-vehicle-row">
        <div className="col-12 col-md-6">
          <label style={labelStyle}>{bk.yearLabel || 'Year'}</label>
          <input type="number" min="1950" max={new Date().getFullYear() + 1} value={value.year}
            onChange={e => onChange({ ...value, year: e.target.value })}
            className="form-control"
          />
        </div>
        <div className="col-12 col-md-6">
          <label style={labelStyle}>{bk.plateLabel || 'License Plate (Optional)'}</label>
          <input placeholder={bk.platePlaceholder || 'ABC-1234'} value={value.license_plate}
            onChange={e => onChange({ ...value, license_plate: e.target.value })}
            className="form-control"
          />
        </div>
      </div>

      {/* Optional VIN + Color (admin mode) */}
      {(showVin || showColor) && (
        <div className="row mb-3 cjpa-vehicle-row">
          {showVin && (
            <div className="col-12 col-md-6">
              <label style={labelStyle}>{t.admin?.customerProfile?.vehicleVin || 'VIN'}</label>
              <input placeholder="VIN" value={value.vin || ''}
                onChange={e => onChange({ ...value, vin: e.target.value })}
                className="form-control"
              />
            </div>
          )}
          {showColor && (
            <div className="col-12 col-md-6">
              <label style={labelStyle}>{t.admin?.customerProfile?.vehicleColor || 'Color'}</label>
              <input placeholder={t.admin?.customerProfile?.vehicleColor || 'Color'} value={value.color || ''}
                onChange={e => onChange({ ...value, color: e.target.value })}
                className="form-control"
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
